/* ============================================================================
 * TRADE_DESK.EXE — treasury, capital formation & governance
 * ----------------------------------------------------------------------------
 * Pillar 4 (Finance / Assets). Reads the on-chain money surface:
 *   • native ETH on Base — gas for everything the OS does
 *   • the Aragon DAO on Base — treasury + proposals
 *   • 0xSplits — revenue distribution address (verified deployed)
 *
 * HONEST STATUS on the parts the brief lists but that do not exist:
 *   • CDP (collateralised debt position): NO protocol is wired. A real CDP
 *     needs a lending market and a liquidation engine. Not built.
 *   • LP: no pool is deployed, so there is nothing to provide liquidity to.
 *   • Revenue distribution via 0xSplits: SplitMain is verified live, but no
 *     split has been created for this treasury.
 *   • Capital token: retired; its replacement is not yet named.
 * ========================================================================= */

import { formatUnits, type Address } from 'viem';
import { useAccount, useBalance } from 'wagmi';
import { base } from 'wagmi/chains';
import { ARAGON_DAO_ADDRESS, GOVERNANCE_LINKS } from '../lib/supercompute';
import { SPLITS_MAIN } from '../lib/immersion';
import { useWallet } from '../hooks/useWallet';

function Row({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <div className="row">
      <span className="row__k">{k}</span>
      <span className={`row__v${mono ? ' mono' : ''}`} style={mono ? { fontSize: 10 } : undefined}>
        {v}
      </span>
    </div>
  );
}

function NotWired({ what }: { what: string }) {
  return (
    <span className="tag tag--warn">{what} — not wired</span>
  );
}

function GasReadout() {
  const { address } = useAccount();
  const { data, isLoading } = useBalance({
    address,
    chainId: base.id,
    query: { enabled: Boolean(address) },
  });
  const low = data ? data.value < 2_000_000_000_000_000n : false;

  return (
    <>
      <span className="eyebrow">base gas reserve</span>
      <div className="panel">
        <Row
          k="Native balance"
          v={
            !address ? (
              <span className="tag tag--muted">connect wallet</span>
            ) : isLoading ? (
              '…'
            ) : data ? (
              `${formatUnits(data.value, data.decimals)} ${data.symbol}`
            ) : (
              <span className="tag tag--warn">unavailable</span>
            )
          }
        />
        <Row
          k="Status"
          v={
            !address ? (
              <span className="tag tag--muted">—</span>
            ) : low ? (
              <span className="tag tag--warn">LOW — top up for attestations</span>
            ) : (
              <span className="tag tag--ok">sufficient</span>
            )
          }
        />
      </div>
    </>
  );
}

function SplitsReadout() {
  return (
    <>
      <span className="eyebrow">0xsplits — revenue distribution</span>
      <div className="panel">
        <Row k="SplitMain" v={SPLITS_MAIN} mono />
        <Row k="Chain" v="Base (8453)" />
        <Row k="Deployed" v={<span className="tag tag--ok">verified on-chain</span>} />
        <Row k="Split created" v={<NotWired what="no split" />} />
        <Row
          k="App"
          v={
            <a href="https://app.splits.org" target="_blank" rel="noreferrer">
              open in Splits ↗
            </a>
          }
        />
      </div>
      <p className="note">
        SplitMain&apos;s code is confirmed at this address on Base and Base
        Sepolia. That makes the address trustworthy; it does not mean this
        treasury has a split configured, so no distribution is shown.
      </p>
    </>
  );
}

function DefiReadout() {
  return (
    <>
      <span className="eyebrow">defi positions</span>
      <div className="panel">
        <Row k="CDP" v={<NotWired what="lending market" />} />
        <Row k="Liquidity pool" v={<span className="tag tag--warn">no pool deployed</span>} />
        <Row k="Stablecoin" v={<NotWired what="no reserve" />} />
        <Row k="Automation" v={<NotWired what="no keeper" />} />
      </div>
      <p className="note note--warn">
        The brief lists CDPs, LPs and stablecoins. None of them exist for this
        treasury: a CDP needs a lending market with a liquidation engine, and
        there is no pool to provide liquidity to. Showing a position here
        would be invented, so the rows read &quot;not wired&quot;.
      </p>
    </>
  );
}

export function TradeDesk() {
  const w = useWallet();

  return (
    <div className="stack">
      <GasReadout />
      <hr className="rule" />
      <SplitsReadout />
      <hr className="rule" />
      <DefiReadout />
      <hr className="rule" />

      <span className="eyebrow">aragon dao — base mainnet</span>
      <div className="panel">
        <Row k="DAO" v={ARAGON_DAO_ADDRESS} mono />
        <Row k="Chain" v="Base (8453)" />
        <Row k="Tagline" v="We Build Exits" />
        <Row
          k="App"
          v={
            <a href={GOVERNANCE_LINKS.aragon} target="_blank" rel="noreferrer">
              open in Aragon ↗
            </a>
          }
        />
        <Row
          k="Explorer"
          v={
            <a href={GOVERNANCE_LINKS.basescanDao} target="_blank" rel="noreferrer">
              basescan ↗
            </a>
          }
        />
      </div>

      <hr className="rule" />

      <span className="eyebrow">session</span>
      <div className="panel">
        <Row
          k="Operator"
          v={w.isConnected ? w.handle : <span className="tag tag--muted">guest</span>}
        />
        <Row k="Chain" v={w.isConnected ? `Base (${base.id})` : 'not connected'} />
      </div>
    </div>
  );
}

/** Exported so other desks format token amounts consistently. */
export function formatToken(v: bigint, decimals = 18, symbol?: string): string {
  const s = formatUnits(v, decimals);
  return symbol ? `${s} ${symbol}` : s;
}

export type { Address };
